import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, AlertCircle, Loader2, Clock } from "lucide-react";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "checking", detail: null });
  const attemptsRef = useRef(0);
  const txnId = params.get("txn");
  const MAX_ATTEMPTS = 6;       // ~12s total before showing pending UI
  const POLL_MS = 2000;

  useEffect(() => {
    if (!txnId) { navigate("/account"); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const r = await api.get(`/billing/status/${txnId}`);
        if (cancelled) return;
        const data = r.data || {};
        if (data.payment_status === "paid") {
          setState({ status: "paid", detail: data });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", detail: data });
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          // Show "pending" UX — payment likely processing; webhook will confirm.
          setState({ status: "pending", detail: data });
          return;
        }
        setState({ status: "checking", detail: data });
        setTimeout(poll, POLL_MS);
      } catch (e) {
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: "failed", detail: null });
          return;
        }
        setTimeout(poll, POLL_MS);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [txnId, navigate]);

  const pkg = state.detail?.package_id?.toUpperCase();

  return (
    <div className="min-h-screen bg-[#FDFDFD] text-zinc-950 px-4 sm:px-8 py-16 flex flex-col items-center" data-testid="billing-success-page">
      <div className="w-full max-w-md border border-zinc-200 bg-white rounded-sm p-8 text-center">
        {state.status === "checking" && (
          <>
            <Loader2 size={36} className="mx-auto text-zinc-500 animate-spin" />
            <h1 className="font-display text-2xl font-bold tracking-tight mt-4">Confirming your payment…</h1>
            <p className="text-sm text-zinc-600 mt-2">Hang tight — we're verifying with Paddle.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 size={44} className="mx-auto text-green-600" />
            <h1 className="font-display text-2xl font-bold tracking-tight mt-4" data-testid="billing-success-heading">Payment successful</h1>
            <p className="text-sm text-zinc-600 mt-2">
              You're now on the <strong>{pkg}</strong> plan. Welcome to the next level.
            </p>
            <Link to="/dashboard" data-testid="goto-dashboard-after-pay"
              className="mt-6 inline-block w-full bg-zinc-950 text-white py-2.5 rounded-sm text-sm font-semibold hover:bg-zinc-800">
              Go to dashboard
            </Link>
          </>
        )}
        {state.status === "pending" && (
          <>
            <Clock size={44} className="mx-auto text-amber-500" />
            <h1 className="font-display text-2xl font-bold tracking-tight mt-4" data-testid="billing-pending-heading">Payment in progress</h1>
            <p className="text-sm text-zinc-600 mt-2">
              Paddle is processing your order. Your plan will activate automatically as soon as the payment clears — usually within a minute. You'll get a confirmation email too.
            </p>
            <Link to="/account" data-testid="back-to-account-pending"
              className="mt-6 inline-block w-full bg-zinc-950 text-white py-2.5 rounded-sm text-sm font-semibold hover:bg-zinc-800">
              Back to account
            </Link>
            <Link to="/dashboard"
              className="mt-2 inline-block w-full border border-zinc-200 py-2.5 rounded-sm text-sm font-semibold hover:bg-zinc-50">
              Go to dashboard
            </Link>
          </>
        )}
        {(state.status === "failed" || state.status === "expired") && (
          <>
            <AlertCircle size={44} className="mx-auto text-red-600" />
            <h1 className="font-display text-2xl font-bold tracking-tight mt-4">
              {state.status === "expired" ? "Checkout expired" : "Couldn't confirm payment"}
            </h1>
            <p className="text-sm text-zinc-600 mt-2">
              {state.status === "expired"
                ? "Your checkout session timed out. Please try again."
                : "We couldn't verify the payment. If you were charged, contact support — no double-charges."}
            </p>
            <Link to="/account" data-testid="back-to-account"
              className="mt-6 inline-block w-full border border-zinc-200 py-2.5 rounded-sm text-sm font-semibold hover:bg-zinc-50">
              Back to account
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
